"use client";

import React, { useEffect, Suspense } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useRouter, useSearchParams } from 'next/navigation';
import { Star, ShieldCheck, Award, Shield } from 'lucide-react';
import { cn } from '@/lib/utils';
import { motion, Variants } from 'framer-motion';
import { Button } from '@/app/components/ui/Button';
import { LandingHeader } from '@/app/components/LandingHeader';
import { Footer } from '@/app/components/Footer';
import { PricingSection } from '@/app/components/PricingSection';
import { Testimonials } from '@/app/components/Testimonials';

// Separate component for search params logic to satisfy Suspense requirement
const MagicLinkHandler = () => {
  const router = useRouter();
  const searchParams = useSearchParams();

  useEffect(() => {
    const code = searchParams.get('code');
    if (code) {
      router.replace(`/auth/callback?code=${code}&next=/write-a-will-online`);
    }
  }, [searchParams, router]);

  return null;
};

const AnimatedText = ({ text, className, delay = 0, stagger = 0.05 }: { text: string, className?: string, delay?: number, stagger?: number }) => {
  const words = text.split(" ");
  return (
    <motion.span
      className={cn("inline-block", className)}
      initial="hidden"
      animate="visible"
      variants={{
        visible: { transition: { staggerChildren: stagger, delayChildren: delay } },
        hidden: {}
      }}
    >
      {words.map((word, i) => (
        <motion.span
          key={i}
          variants={{
            hidden: { opacity: 0, y: 5 },
            visible: { opacity: 1, y: 0, transition: { duration: 0.3 } }
          }}
          className="inline-block mr-[0.25em] last:mr-0"
        >
          {word}
        </motion.span>
      ))}
    </motion.span>
  );
};

export default function LandingPage() {
  const [isSupportOnline, setIsSupportOnline] = React.useState(true);

  // No router/searchParams here anymore

  React.useEffect(() => {
    const checkSupportStatus = () => {
      const now = new Date();
      // Get current time in GMT/UTC
      const day = now.getUTCDay(); // 0 is Sunday, 1 is Monday, ..., 6 is Saturday
      const hour = now.getUTCHours();

      // Monday to Friday: 1 to 5
      const isWeekday = day >= 1 && day <= 5;
      // 9am to 6pm GMT: 9 to 17 (18:00 is not included as it's the end of the hour)
      const isWorkingHours = hour >= 9 && hour < 18;

      setIsSupportOnline(isWeekday && isWorkingHours);
    };

    checkSupportStatus();
    // Re-check every minute
    const interval = setInterval(checkSupportStatus, 60000);
    return () => clearInterval(interval);
  }, []);

  const containerVariants: Variants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants: Variants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-background text-foreground font-body overflow-x-hidden selection:bg-primary selection:text-white">
      {/* Suspense wrapper for the magic link handler */}
      <Suspense fallback={null}>
        <MagicLinkHandler />
      </Suspense>

      <LandingHeader transparent />

      <main className="flex-1">

        {/* Hero Section - Improved Layout */}
        <section className="relative min-h-screen border-b-2 border-foreground/10 grid grid-cols-1 lg:grid-cols-12 bg-white overflow-hidden">

          {/* Background Elements */}
          <motion.div
            className="absolute inset-0 z-0 opacity-100 pointer-events-none"
            initial={{ scale: 1.0, rotate: 0 }}
            animate={{ scale: 1.25, rotate: 2 }}
            transition={{
              duration: 20,
              ease: "easeInOut",
              repeat: Infinity,
              repeatType: "reverse"
            }}
          >
            <Image
              src="/draftwill imagery/hero.svg"
              alt="Background pattern"
              fill
              className="object-cover object-center"
              priority
            />
          </motion.div>

          {/* Left: Content & Value Prop */}
          <motion.div
            className="lg:col-span-7 px-8 pt-32 pb-8 md:px-16 md:pt-40 md:pb-16 lg:px-24 lg:pt-48 lg:pb-24 flex flex-col justify-center relative z-10"
            initial="hidden"
            animate="visible"
            variants={containerVariants}
          >

            <motion.div
              variants={itemVariants}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/5 border border-primary/15 text-primary text-sm font-semibold shadow-sm mb-6 w-fit"
            >
              <ShieldCheck className="w-4 h-4" />
              <span>Money back guarantee</span>
            </motion.div>

            <motion.h1 variants={itemVariants} className="font-display text-5xl md:text-7xl leading-[0.95] tracking-tight mb-8 text-balance text-primary">
              Your Will? <br /> <span className="text-4xl md:text-6xl text-teal">Consider it Done</span>
            </motion.h1>

            <motion.p variants={itemVariants} className="max-w-xl text-lg md:text-xl font-light text-muted-foreground mb-10 leading-relaxed">
              Secure your family&apos;s future with a legally binding Will in under 15 minutes. Every will checked by an expert.
            </motion.p>

            <motion.div variants={itemVariants} className="flex flex-col sm:flex-row gap-5 items-start sm:items-center mb-12">
              <Link href="/write-a-will-online" className="w-full sm:w-auto">
                <Button size="lg" className="w-full sm:w-auto text-lg h-16 px-8 shadow-[4px_4px_0px_0px_rgba(45,102,89,1)] hover:translate-y-[2px] hover:translate-x-[2px] hover:shadow-[2px_2px_0px_0px_rgba(45,102,89,1)] transition-all border-2 border-foreground">
                  Start Your Will
                </Button>
              </Link>
            </motion.div>

            <motion.div variants={itemVariants}>
              <div className="w-[30%] border-t border-foreground/5 mb-8" />
              <div className="flex flex-wrap gap-8 text-sm font-medium text-muted-foreground">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="w-5 h-5 text-primary" />
                  <span className="text-foreground font-bold">Peace of mind</span>
                </div>
                <div className="flex items-center gap-2">
                  <Star className="w-5 h-5 text-primary fill-primary/10" />
                  <span className="text-foreground font-bold">Every Will reviewed</span>
                </div>
                <div className="flex items-center gap-2">
                  <Award className="w-5 h-5 text-primary" />
                  <span className="text-foreground font-bold">Affordable and Secure</span>
                </div>
              </div>
            </motion.div>
          </motion.div>

          {/* Right: Visual Abstract */}
          <div className="lg:col-span-5 relative overflow-hidden flex flex-col justify-center items-center p-12 min-h-[500px] lg:min-h-auto border-l-0 lg:border-l-0">

            {/* Circle Decorative Elements */}
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 1.5, ease: "easeOut" }}
              className="absolute top-1/4 right-1/4 w-64 h-64 bg-primary/5 rounded-full blur-3xl"
            />
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 1.5, delay: 0.2, ease: "easeOut" }}
              className="absolute bottom-1/4 left-1/4 w-64 h-64 bg-secondary/20 rounded-full blur-3xl"
            />

            <motion.div
              initial={{ opacity: 0, y: 40, rotate: 0 }}
              animate={{ opacity: 1, y: 0, rotate: 0 }}
              transition={{ duration: 0.8, delay: 0.4, ease: "easeOut" }}
              whileHover={{ scale: 1.02 }}
              className="relative z-10 w-full max-w-sm mx-auto group perspective-1000"
            >
              {/* Main Card */}
              <div className="bg-[#FDFDFB] border-2 border-foreground p-8 pb-16 relative transition-transform duration-500 ease-out min-h-[550px] flex flex-col">

                {/* Inner Border for Official Look */}
                <div className="absolute inset-3 border border-foreground/20 pointer-events-none" />

                {/* Header */}
                <div className="flex justify-between items-center mb-8 pb-3 border-b-2 border-foreground/10">
                  <div>
                    <div className="font-display text-2xl not-italic">
                      <AnimatedText text="Last Will & Testament" delay={1.4} />
                    </div>
                  </div>
                  <div className="w-12 h-12 rounded-full border-2 border-cta/30 flex items-center justify-center bg-cta/5 relative">
                    <div className="absolute inset-0 border border-cta rounded-full scale-75 opacity-50" />
                    <Shield className="w-5 h-5 text-cta" />
                  </div>
                </div>

                {/* Body Lines */}
                <div className="space-y-4 text-justify">
                  <p className="font-display text-sm leading-relaxed text-foreground/90">
                    <span className="font-bold"><AnimatedText text="THIS IS THE LAST WILL AND TESTAMENT" delay={2.4} /></span>
                    <span className="inline-block w-1" />
                    <AnimatedText text="of me, Will Done." delay={3.0} />
                  </p>

                  <div className="font-display text-sm leading-relaxed text-foreground/90">
                    <p className="font-bold mb-1"><AnimatedText text="1. REVOCATION" delay={3.5} /></p>
                    <p><AnimatedText text="I HEREBY REVOKE all former Wills and testamentary dispositions made by me." delay={3.8} /></p>
                  </div>

                  <div className="font-display text-sm leading-relaxed text-foreground/90">
                    <p className="font-bold mb-1"><AnimatedText text="2. EXECUTORS" delay={4.5} /></p>
                    <p className="mb-1"><AnimatedText text="I APPOINT the following person(s) to be the Executor(s) and Trustee(s) of this my Will:" delay={4.8} /></p>
                    <ul className="list-disc pl-5 text-foreground/80 mt-2 mb-4">
                      <motion.li
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 5.4, duration: 0.5 }}
                      >
                        <AnimatedText text="Person X of 1 Hope Street, London, W1U 3PP" delay={5.4} className="inline" />
                      </motion.li>
                    </ul>
                  </div>
                </div>

                {/* Footer / Seal */}
                <div className="mt-auto pt-6 border-t-2 border-dashed border-foreground/20 flex justify-start items-end gap-24">
                  <div className="flex flex-col gap-1">
                    <div className="relative h-12 w-32 -ml-2 mb-1">
                      <svg viewBox="0 0 150 60" className="w-full h-full -rotate-2">
                        {/* Will */}
                        <motion.path
                          d="M 15 35 C 15 50 25 50 30 35 C 35 50 45 50 50 35 Q 52 45 52 45 L 55 40 L 55 45 Q 58 20 60 45 Q 63 20 65 45 Q 70 45 75 40"
                          fill="transparent"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          className="text-foreground/80"
                          initial={{ pathLength: 0, opacity: 0 }}
                          animate={{ pathLength: 1, opacity: 1 }}
                          transition={{ duration: 1.5, ease: "easeInOut", delay: 6.5 }}
                        />
                        {/* Dot */}
                        <motion.path
                          d="M 55 32 L 55 34"
                          fill="transparent"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          className="text-foreground/80"
                          initial={{ pathLength: 0, opacity: 0 }}
                          animate={{ pathLength: 1, opacity: 1 }}
                          transition={{ duration: 0.1, delay: 8.0 }}
                        />
                        {/* D */}
                        <motion.path
                          d="M 90 25 L 90 45 C 85 45 85 45 90 45 C 115 45 115 25 90 25"
                          fill="transparent"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          className="text-foreground/80"
                          initial={{ pathLength: 0, opacity: 0 }}
                          animate={{ pathLength: 1, opacity: 1 }}
                          transition={{ duration: 0.8, ease: "easeInOut", delay: 8.2 }}
                        />
                        {/* one */}
                        <motion.path
                          d="M 112 43 C 108 43 108 49 112 49 C 116 49 116 43 112 43 Q 115 43 117 45 L 117 49 L 117 45 Q 119 42 122 45 L 122 49 Q 124 49 126 44 C 128 42 122 44 123 47 C 124 50 132 48 135 47"
                          fill="transparent"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          className="text-foreground/80"
                          initial={{ pathLength: 0, opacity: 0 }}
                          animate={{ pathLength: 1, opacity: 1 }}
                          transition={{ duration: 0.8, ease: "easeInOut", delay: 9.0 }}
                        />
                      </svg>
                    </div>
                    <div className="text-[10px] text-teal uppercase tracking-widest">
                      <AnimatedText text="Signed & Witnessed" delay={4.8} />
                    </div>
                  </div>

                  {/* Wax Seal Graphic */}
                  <motion.div
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ delay: 10.0, type: "spring", stiffness: 200, damping: 20 }}
                    className="relative w-16 h-16 -mb-2"
                  >
                    <Image
                      src="/draftwill imagery/approvalstamp.jpg"
                      alt="W Seal"
                      fill
                      className="object-contain"
                    />
                  </motion.div>
                </div>
              </div>

              {/* Floating Valid Badge */}
              <motion.div
                initial={{ scale: 0, rotate: 15 }}
                animate={{ scale: 1, rotate: 5 }}
                transition={{ delay: 0.8, type: "spring" }}
                className="absolute -top-6 -right-10 bg-[#FDFDFB] text-foreground px-5 py-4 border border-foreground shadow-lg group-hover:rotate-[8deg] transition-transform duration-300 flex flex-col items-center gap-1 z-20"
              >
                <div className="flex gap-1 mb-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-3 h-3 fill-current text-cta" />
                  ))}
                </div>
                <div className="font-display text-xs text-center leading-tight uppercase tracking-wider text-muted-foreground">Every Will</div>
                <div className="font-display font-bold text-sm text-center leading-tight">Professionally<br />Reviewed</div>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* Social Proof / Trust Section */}
        <Testimonials />

        {/* Pricing */}
        <PricingSection id="pricing" className="bg-secondary/5 border-t border-b border-foreground/10" />

        {/* Speak to Experts Section */}
        <section className="py-24 px-6 bg-primary text-primary-foreground border-y-2 border-primary-foreground/10 relative overflow-hidden">
          {/* Decorative background element */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3 pointer-events-none" />

          <div className="container mx-auto max-w-5xl relative z-10">
            <div className="flex flex-col md:flex-row items-center gap-12 md:gap-16">

              {/* Left: Text Content */}
              <div className="flex-1 text-center md:text-left">
                <h2 className="text-4xl md:text-5xl mb-6 text-white">Speak to our experts today</h2>
                <p className="text-xl text-primary-foreground/90 font-light mb-8 leading-relaxed">
                  Not sure where to start? Our team is here to help simplify the process. Whether you have questions about complex estates or just need a hand getting started, we&apos;re here to help.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start">
                  <Link href="/get-in-touch">
                    <Button size="lg" className="w-full sm:w-auto text-lg px-8 py-6 h-auto shadow-none hover:translate-y-[2px] hover:translate-x-[2px] hover:shadow-none transition-all border-2 border-white bg-white text-primary hover:bg-white/90">
                      Request a callback
                    </Button>
                  </Link>
                  <a href="mailto:hello@willdone.co.uk">
                    <Button size="lg" className="w-full sm:w-auto text-lg px-8 py-6 h-auto shadow-none hover:translate-y-[2px] hover:translate-x-[2px] hover:shadow-none transition-all border-2 border-white bg-transparent text-white hover:bg-white/10">
                      Email Us
                    </Button>
                  </a>
                </div>
              </div>

              {/* Right: Visual/Avatars */}
              <div className="flex flex-col items-center justify-center">
                <div className="flex -space-x-6 mb-6">
                  {[
                    { src: "/draftwill imagery/team/p1.jpeg", alt: "Team Member 1" },
                    { src: "/draftwill imagery/team/p2.jpeg", alt: "Team Member 2" },
                    { src: "/draftwill imagery/team/p3.jpeg", alt: "Team Member 3" }
                  ].map((p, i) => (
                    <div key={i} className="w-24 h-24 rounded-full border-[6px] border-primary bg-muted flex items-center justify-center relative shadow-sm overflow-hidden">
                      <Image
                        src={p.src}
                        alt={p.alt}
                        fill
                        sizes="96px"
                        className="object-cover"
                      />
                    </div>
                  ))}
                </div>
                <div className="bg-white/10 backdrop-blur-sm px-6 py-2 rounded-full border border-white/20 shadow-sm flex items-center gap-2 mb-3">
                  <div className={cn(
                    "w-2.5 h-2.5 rounded-full",
                    isSupportOnline ? "bg-emerald-400 animate-pulse" : "bg-red-400"
                  )} />
                  <span className="text-sm font-medium text-white">
                    UK Support Team {isSupportOnline ? 'Online' : 'Offline'}
                  </span>
                </div>
                <div className="text-primary-foreground/60 text-[11px] font-medium uppercase tracking-widest">
                  Available Mon-Fri, 9am-6pm
                </div>
              </div>

            </div>
          </div>
        </section>



        {/* FAQ Section */}
        <section id="faq" className="py-24 px-6 bg-background">
          <div className="container mx-auto max-w-4xl">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="font-display text-5xl mb-12 text-center text-primary"
            >
              Common Questions
            </motion.h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-12">
              {[
                { q: "Is this legally binding?", a: "Yes. In England & Wales, a Will is legally valid as long as it is in writing, signed by you, and witnessed by two people. Our platform generates a document that meets all these legal requirements." },
                { q: "Can I update my Will later?", a: "Ideally, you should make a new Will if your circumstances change significantly. Since our cost is so low, you can simply create a fresh Will whenever you need to update it." },
                { q: "Do I need a solicitor?", a: "Not for a standard Will. Solicitors are useful for very complex estates (e.g., overseas assets, complex trusts), but for the vast majority of people, our service is perfectly sufficient." },
                { q: "What happens after I pay?", a: "We will review your answers and our Will experts will make sure your Will accurately reflects your wishes. Once happy, they will email you your PDF and information on what comes next." },
                { q: "What if I'm not satisfied?", a: "We offer a 30 day money back guarantee. If you're not happy with the service or the document produced, just let us know and we'll refund you in full." },
                { q: "How long does it take?", a: "Most people complete their Will in less than 15 minutes. Our step-by-step process makes it quick and easy to ensure your wishes are recorded correctly." }
              ].map((faq, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="space-y-3"
                >
                  <h3 className="font-bold text-xl">{faq.q}</h3>
                  <p className="text-muted-foreground leading-relaxed">{faq.a}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>


        <Footer />

      </main>
    </div >
  );
}
